import { UTCTimestamp } from 'lightweight-charts';

import { RangeListEnum } from '../../pages/portfolio-performance/types';
import {
  calendarWindow,
  ExtendedRange,
  isCalendarRange,
} from '../../pages/portfolio-performance/range';
import { IPortfolioPerformanceHistorical } from './portfolio-performance.types';

// One selectable series to overlay. There are no "strategy" vs "benchmark"
// roles — a real trading account and a Buy-and-Hold reference are both just a
// daily-return history, treated identically.
export interface ComparisonSeriesInput {
  // Unique key (the account name) — also what the chart keys its series on.
  key: string;
  label: string;
  color: string;
  historical: IPortfolioPerformanceHistorical | null | undefined;
}

export interface ComparisonSeriesOutput {
  key: string;
  label: string;
  color: string;
  // Cumulative percentage change from the window start: (index_t - 1) * 100,
  // where index rebases to 1.0 at this series' first day in the window.
  data: { time: UTCTimestamp; value: number }[];
  // Day-timestamp (seconds) this series' line actually begins at. Equals the
  // window start for a series with data there; LATER for a series whose
  // history starts after the window start (e.g. an account opened mid-window)
  // — the UI uses it to note "line begins later" rather than dropping it or
  // rebasing everyone to the shortest series.
  rebasedAt: UTCTimestamp | null;
}

const MONTH_INDEX: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

// Trailing-day window per range, counted back from the latest day in the
// common grid (not wall-clock today — the series can lag a day). Mirrors the
// existing equity chart's RANGE_DAYS so the two controls mean the same thing.
const RANGE_DAYS: Record<RangeListEnum, number | null> = {
  [RangeListEnum.D1]: 2,
  [RangeListEnum.D5]: 5,
  [RangeListEnum.M1]: 30,
  [RangeListEnum.M3]: 90,
  [RangeListEnum.M6]: 180,
  [RangeListEnum.Y1]: 365,
  [RangeListEnum.Y2]: 730,
  [RangeListEnum.Y3]: 1095,
  [RangeListEnum.ALL]: null,
};

// Flattens one series' daily returns to a chronological {dayTs -> return} map.
// Only days carrying a numeric `return` contribute; a day with no return is a
// non-trading day that will carry the prior index value forward.
const flattenReturnsByDay = (
  historical: IPortfolioPerformanceHistorical,
): Map<number, number> => {
  const byDay = new Map<number, number>();

  for (const [year, yearData] of Object.entries(historical)) {
    for (const [month, monthData] of Object.entries(yearData.months)) {
      const monthIndex = MONTH_INDEX[month];
      if (monthIndex === undefined) continue;

      for (const [day, dayData] of Object.entries(monthData.days)) {
        if (typeof dayData.return !== 'number' || !Number.isFinite(dayData.return)) continue;
        const ts = Date.UTC(Number(year), monthIndex, Number(day)) / 1000;
        byDay.set(ts, dayData.return);
      }
    }
  }

  return byDay;
};

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// "DD Mon YYYY" in UTC — for the legend's "(from …)" late-start note.
export const formatComparisonDate = (time: UTCTimestamp): string => {
  const d = new Date((time as number) * 1000);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// Resolves the in-window grid and the baseline convention for a range.
//
// Trailing ranges (1D..ALL): window = trailing RANGE_DAYS counted back from the
// last grid day; the baseline day (each series' first in-window day) is rebased
// to 0% and its OWN return is NOT applied.
//
// Calendar ranges (MTD/YTD): window = grid days in the current calendar month /
// year, "current" from new Date() on the same basis as performance-card's
// applyScope. The baseline is the PRIOR-PERIOD CLOSE, so the first in-period day
// DOES show its return — the series' latest cumulative % then equals the
// compounded in-period daily returns, matching the Overview table's
// mtdReturnRaw/ytdReturnRaw (return-to-date). A series with no data before the
// period boundary falls back to the trailing convention (first in-period day at
// 0%, its return not applied).
const resolveWindow = (
  grid: number[],
  range: ExtendedRange,
): { windowGrid: number[]; applyFirstReturnIfPriorData: boolean; periodBoundary: number | null } => {
  if (!isCalendarRange(range)) {
    const days = RANGE_DAYS[range];
    const windowStart = days === null ? grid[0] : grid[Math.max(0, grid.length - days)];
    return {
      windowGrid: grid.filter((ts) => ts >= windowStart),
      applyFirstReturnIfPriorData: false,
      periodBoundary: null,
    };
  }

  const { inPeriod, boundary } = calendarWindow(range);
  return {
    windowGrid: grid.filter(inPeriod),
    applyFirstReturnIfPriorData: true,
    periodBoundary: boundary,
  };
};

// Common daily grid: union of every series' trading days, sorted ascending.
const commonGrid = (returnMaps: Map<number, number>[]): number[] => {
  const gridSet = new Set<number>();
  for (const map of returnMaps) {
    for (const ts of map.keys()) gridSet.add(ts);
  }
  return Array.from(gridSet).sort((a, b) => a - b);
};

// One selected series' daily returns restricted to the window for `range`,
// keyed by day timestamp (seconds). This is the shared, window-aligned
// daily-return view the Indexed overlay rebases from and the correlation
// matrix / scatter consume — so every comparison in the panel means the SAME
// set of in-window days. Only days the series actually traded carry a value.
export interface WindowedReturnsSeries {
  key: string;
  label: string;
  returnsByDay: Map<number, number>;
}

// Per-series in-window daily returns over the common grid for `range`. The
// window resolution (trailing RANGE_DAYS vs MTD/YTD calendar) is exactly the
// one buildIndexedComparison uses; the calendar prior-close baseline nuance is
// irrelevant here because a correlation uses each day's return independently.
export const buildWindowedReturns = (
  series: ComparisonSeriesInput[],
  range: ExtendedRange,
): WindowedReturnsSeries[] => {
  const returnMaps = series.map((s) =>
    s.historical ? flattenReturnsByDay(s.historical) : new Map<number, number>(),
  );
  const grid = commonGrid(returnMaps);
  if (grid.length === 0) {
    return series.map((s) => ({ key: s.key, label: s.label, returnsByDay: new Map() }));
  }

  const { windowGrid } = resolveWindow(grid, range);
  const windowDays = new Set(windowGrid);

  return series.map((s, i) => {
    const inWindow = new Map<number, number>();
    for (const [ts, r] of returnMaps[i]) {
      if (windowDays.has(ts)) inWindow.set(ts, r);
    }
    return { key: s.key, label: s.label, returnsByDay: inWindow };
  });
};

// Builds the indexed cumulative-% overlay for every selected series over a
// common daily date grid. index_t = index_(t-1) * (1 + r_t); plotted value =
// (index_t - 1) * 100. See resolveWindow for the per-range baseline convention.
//
// Series carry different histories: a series with no data at the exact window
// start rebases at its FIRST available day inside the window and its line
// begins there — the rest keep rebasing at the true window start.
export const buildIndexedComparison = (
  series: ComparisonSeriesInput[],
  range: ExtendedRange,
): ComparisonSeriesOutput[] => {
  const returnMaps = series.map((s) =>
    s.historical ? flattenReturnsByDay(s.historical) : new Map<number, number>(),
  );

  const grid = commonGrid(returnMaps);
  if (grid.length === 0) {
    return series.map((s) => ({ key: s.key, label: s.label, color: s.color, data: [], rebasedAt: null }));
  }

  const { windowGrid, applyFirstReturnIfPriorData, periodBoundary } = resolveWindow(grid, range);

  // Calendar windows (MTD/YTD): the last grid day BEFORE the period boundary —
  // the prior-period close. Plotted at 0% as the baseline point so the line
  // visibly starts at zero and the first in-period day then moves to its own
  // return (daily returns are to-midnight, so e.g. 1 Oct is a real return, not
  // zero). Null when nothing trades before the boundary (falls back to trailing).
  const calendarBaselineTs =
    periodBoundary !== null
      ? ([...grid].reverse().find((ts) => ts < periodBoundary) ?? null)
      : null;

  return series.map((s, i) => {
    const returnsByDay = returnMaps[i];

    // First grid day in the window where this series actually traded — its
    // rebase anchor (window start for a full-history series, later otherwise).
    const rebaseTs = windowGrid.find((ts) => returnsByDay.has(ts));
    if (rebaseTs === undefined) {
      return { key: s.key, label: s.label, color: s.color, data: [], rebasedAt: null };
    }

    // Calendar windows anchor at the prior-period close: apply the first
    // in-period day's return (so the endpoint reconciles with return-to-date),
    // but only if this series actually has a data point before the boundary.
    const hasPriorData =
      periodBoundary !== null &&
      Array.from(returnsByDay.keys()).some((ts) => ts < periodBoundary);
    const applyFirstReturn = applyFirstReturnIfPriorData && hasPriorData;

    const data: { time: UTCTimestamp; value: number }[] = [];
    let index = 1;
    // Calendar window with prior data: prepend the prior-period close at 0% so
    // the line starts at zero before the first in-period day applies its return.
    if (applyFirstReturn && calendarBaselineTs !== null) {
      data.push({ time: calendarBaselineTs as UTCTimestamp, value: 0 });
    }
    for (const ts of windowGrid) {
      if (ts < rebaseTs) continue;
      if (ts === rebaseTs && !applyFirstReturn) {
        // Baseline day: rebased to 0%, its own return is not applied.
        data.push({ time: ts as UTCTimestamp, value: 0 });
        continue;
      }
      const r = returnsByDay.get(ts);
      // Non-trading day for this series: carry the prior index forward (flat).
      if (typeof r === 'number') index *= 1 + r;
      data.push({ time: ts as UTCTimestamp, value: (index - 1) * 100 });
    }

    return {
      key: s.key,
      label: s.label,
      color: s.color,
      data,
      rebasedAt: rebaseTs as UTCTimestamp,
    };
  });
};
